/**
 * useKPIMetricsOptimized - Single RPC for all KPI metrics
 * Replaces 2 separate queries with 1 optimized call
 * Target: <5ms execution time
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';

interface KPIMetricData {
  id: string;
  metric_date: string;
  metric_name: string;
  metric_value: number;
  target_value: number;
  previous_value: number;
  variance_percentage: number;
  trend: 'up' | 'down' | 'stable';
  category: string;
  unit: string;
}

interface KPIMetricsFilters {
  dateFrom: string;
  dateTo: string;
}

export const useKPIMetricsOptimized = (filters: KPIMetricsFilters) => {
  const { currentHotel } = useOptimizedHotel();

  const query = useQuery({
    queryKey: ['kpi-metrics-optimized', currentHotel?.id, filters.dateFrom, filters.dateTo],
    queryFn: async () => {
      if (!currentHotel?.id) return [];

      console.log('🚀 Fetching optimized KPI metrics...');
      const startTime = performance.now();

      const { data, error } = await supabase.rpc('get_kpi_metrics_for_hotel', {
        p_hotel_id: currentHotel.id,
        p_date_from: filters.dateFrom,
        p_date_to: filters.dateTo
      });

      const duration = performance.now() - startTime;
      console.log(`✅ KPI metrics loaded in ${duration.toFixed(1)}ms`);

      if (error) {
        console.error('❌ KPI Metrics Error:', error);
        throw error;
      }

      const stats = data?.[0] || {
        total_revenue: 0,
        occupancy_rate: 0,
        adr: 0,
        conversion_rate: 0,
        total_bookings: 0,
        confirmed_bookings: 0,
        total_rooms: 0,
        days_in_period: 1
      };

      // Transform to KPI format
      const kpiMetrics: KPIMetricData[] = [
        {
          id: '1',
          metric_date: filters.dateTo,
          metric_name: 'Total Revenue',
          metric_value: Number(stats.total_revenue) || 0,
          target_value: Number(stats.total_revenue) * 1.2,
          previous_value: Number(stats.total_revenue) * 0.9,
          variance_percentage: 10,
          trend: 'up',
          category: 'Financial',
          unit: 'RUB',
        },
        {
          id: '2',
          metric_date: filters.dateTo,
          metric_name: 'Occupancy Rate',
          metric_value: Number(stats.occupancy_rate) || 0,
          target_value: 85,
          previous_value: Number(stats.occupancy_rate) - 5,
          variance_percentage: 5,
          trend: Number(stats.occupancy_rate) > 75 ? 'up' : 'stable',
          category: 'Operations',
          unit: '%',
        },
        {
          id: '3',
          metric_date: filters.dateTo,
          metric_name: 'ADR',
          metric_value: Number(stats.adr) || 0,
          target_value: Number(stats.adr) * 1.15,
          previous_value: Number(stats.adr) * 0.95,
          variance_percentage: 5,
          trend: 'up',
          category: 'Financial',
          unit: 'RUB',
        },
        {
          id: '4',
          metric_date: filters.dateTo,
          metric_name: 'Conversion Rate',
          metric_value: Number(stats.conversion_rate) || 0,
          target_value: 70,
          previous_value: Number(stats.conversion_rate) - 3,
          variance_percentage: 3,
          trend: Number(stats.conversion_rate) > 60 ? 'up' : 'down',
          category: 'Marketing',
          unit: '%',
        },
        {
          id: '5',
          metric_date: filters.dateTo,
          metric_name: 'Total Bookings',
          metric_value: Number(stats.total_bookings) || 0,
          target_value: Number(stats.total_bookings) * 1.2,
          previous_value: Number(stats.total_bookings) * 0.85,
          variance_percentage: 15,
          trend: 'up',
          category: 'Operations',
          unit: 'count',
        },
      ];

      return kpiMetrics;
    },
    enabled: !!currentHotel?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const data = query.data || [];

  const latestMetrics = data.reduce((acc, metric) => {
    if (!acc[metric.metric_name] || new Date(metric.metric_date) > new Date(acc[metric.metric_name].metric_date)) {
      acc[metric.metric_name] = metric;
    }
    return acc;
  }, {} as Record<string, KPIMetricData>);

  const metricsByCategory = data.reduce((acc, metric) => {
    if (!acc[metric.category]) {
      acc[metric.category] = [];
    }
    acc[metric.category].push(metric);
    return acc;
  }, {} as Record<string, KPIMetricData[]>);

  const performanceAlerts = Object.values(latestMetrics).filter(metric => {
    if (metric.target_value && metric.metric_value < metric.target_value * 0.9) {
      return true;
    }
    if (metric.variance_percentage && Math.abs(metric.variance_percentage) > 20) {
      return true;
    }
    return false;
  });

  return { 
    data, 
    loading: query.isLoading, 
    error: query.error ? String(query.error) : null, 
    latestMetrics, 
    metricsByCategory, 
    performanceAlerts, 
    refetch: query.refetch 
  };
};
